/**
 * Small storefront behaviours that belong to the brand layer rather than to a
 * single section.
 *
 * WHY THIS IS AN ASSET AND NOT A {% javascript %} BLOCK
 * Shopify collects {% stylesheet %} and {% javascript %} from sections. Snippets
 * are not a documented home for the javascript tag, so a control defined in a
 * snippet cannot rely on it -- it renders inert on the storefront while looking
 * fine in a local preview. Both behaviours here are attached to snippets, so
 * they live in a real asset loaded from the layout.
 *
 * Everything is progressive enhancement: the collection density control is a
 * working button before this runs, and Buy it now falls back to the form's own
 * submit path.
 */

/* v2: the default flipped to four-up on desktop, so choices stored under the
   old key (which mostly recorded the old roomy default) are left behind. */
const DENSITY_KEY = 'pa:collection-density-v2';

/* ---------------------------------------------------------------------------
 * Collection grid density
 * ------------------------------------------------------------------------ */

class PaGridDensity extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-pa-density]');
    if (!this.button) return;

    this.grid = document.querySelector('#product-grid .grid, .collection .grid');
    /* The landing collections list categories rather than products, and a
       density switch with no grid to act on is a control that does nothing. */
    if (!this.grid) {
      this.hidden = true;
      return;
    }

    this.onClick = this.toggle.bind(this);
    /* Every viewer starts dense -- four-up on a laptop, two-up on a phone --
       and the button opens it up to two-up / one-up. A stored choice wins. */
    const roomy = this.read() === 'roomy';
    this.apply(roomy);
    this.button.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
  }

  /* Storage throws outright in a private window rather than returning null, and
     a remembered column count is never worth an exception that stops the rest
     of the page upgrading. */
  read() {
    try {
      return localStorage.getItem(DENSITY_KEY);
    } catch {
      return null;
    }
  }

  write(value) {
    try {
      localStorage.setItem(DENSITY_KEY, value);
    } catch {
      /* The class on the grid is still correct for this view. */
    }
  }

  apply(roomy) {
    this.grid.classList.toggle('pa-grid-density--roomy', roomy);
    this.button.setAttribute('aria-pressed', String(roomy));
  }

  toggle() {
    const roomy = !this.grid.classList.contains('pa-grid-density--roomy');
    this.apply(roomy);
    this.write(roomy ? 'roomy' : 'dense');
  }
}

if (!customElements.get('pa-grid-density')) {
  customElements.define('pa-grid-density', PaGridDensity);
}

/* --------------------------------------------------------------------------
 * Card colour swatches: hovering a circle previews that colour's photograph in
 * the card, and leaving puts the original back. The circle is a real link to
 * the variant, so without this script it still works.
 * ------------------------------------------------------------------------ */
/* The card's own hover photograph sits on top of the first image, so while a
   swatch is previewed the card is flagged and CSS hides that second image. */
function paSwatchImage(swatch, previewing) {
  const card = swatch.closest('.card-wrapper, .product-card-wrapper');
  card?.classList.toggle('pa-swatch-preview', previewing);
  return card?.querySelector('.card__media img');
}

document.addEventListener('pointerover', (event) => {
  const swatch = event.target.closest?.('[data-pa-swatch-img]');
  if (!swatch) return;
  const img = paSwatchImage(swatch, true);
  if (!img) return;
  if (!img.dataset.paOriginalSrc) {
    img.dataset.paOriginalSrc = img.src;
    img.dataset.paOriginalSrcset = img.srcset || '';
  }
  img.srcset = '';
  img.src = swatch.dataset.paSwatchImg;
});

document.addEventListener('pointerout', (event) => {
  const swatch = event.target.closest?.('[data-pa-swatch-img]');
  if (!swatch || swatch.contains(event.relatedTarget)) return;
  const img = paSwatchImage(swatch, false);
  if (!img || !img.dataset.paOriginalSrc) return;
  img.src = img.dataset.paOriginalSrc;
  img.srcset = img.dataset.paOriginalSrcset;
});

/* ---------------------------------------------------------------------------
 * Buy it now
 *
 * Shopify's own Buy it now is a submit button named `checkout` on the product
 * form: the browser posts to /cart/add, and the presence of that name makes
 * Shopify redirect to checkout instead of back to the cart.
 *
 * Dawn's <product-form> intercepts the form's submit event to add over AJAX, so
 * a plain submit button would only ever add to the cart. HTMLFormElement.submit()
 * does not fire submit listeners, which is exactly the escape hatch needed -- but
 * it also means the button's own name/value is not sent, so the `checkout` flag
 * goes in as a hidden input first.
 * ------------------------------------------------------------------------ */

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-pa-buy-now]');
  if (!button || button.disabled) return;

  const form = button.closest('form');
  if (!form) return;

  event.preventDefault();

  if (!form.querySelector('input[name="checkout"]')) {
    const flag = document.createElement('input');
    flag.type = 'hidden';
    flag.name = 'checkout';
    flag.value = '';
    form.appendChild(flag);
  }

  button.setAttribute('aria-disabled', 'true');
  form.submit();
});
